import * as React from 'react';

import { scheduleReadyAttribute, toggleOverflowAttributes } from './utils';

interface IUseScrollEdgesParams {
  containerRef: React.RefObject<HTMLElement | null>;
  leftEdgeRef: React.RefObject<HTMLElement | null>;
  rightEdgeRef: React.RefObject<HTMLElement | null>;
  isEnabled: boolean;
}

/**
 * Следит за горизонтальными краями скролл-контейнера: ставит на контейнер атрибуты
 * data-overflow-left / data-overflow-right, когда за соответствующим краем есть
 * скрытый контент (элемент-сентинел края не пересекается с видимой областью).
 *
 * Атрибуты пишутся напрямую в DOM без setState — ноль ре-рендеров (готовый useInView
 * из react-intersection-observer не подходит: он делает setState на каждое пересечение).
 *
 * Контракт: сентинелы — первый и последний дети контейнера, живут весь его lifecycle
 * (observer подписывается на них один раз); isEnabled=false, когда контейнер не отрендерен.
 */
export function useScrollEdges({ containerRef, leftEdgeRef, rightEdgeRef, isEnabled }: IUseScrollEdgesParams): void {
  React.useEffect(() => {
    const container = containerRef.current;
    const leftEdge = leftEdgeRef.current;
    const rightEdge = rightEdgeRef.current;

    if (!isEnabled || !container || !leftEdge || !rightEdge) {
      return;
    }

    let cancelReadySchedule: (() => void) | null = null;

    const observer = new IntersectionObserver(
      entries => {
        toggleOverflowAttributes(container, entries, leftEdge);

        cancelReadySchedule ??= scheduleReadyAttribute(container);
      },
      { root: container, threshold: 0 },
    );

    observer.observe(leftEdge);
    observer.observe(rightEdge);

    return () => {
      observer.disconnect();
      cancelReadySchedule?.();
    };
  }, [isEnabled, containerRef, leftEdgeRef, rightEdgeRef]);
}
